import { RefObject, createElement, useCallback, useEffect, useState } from "react";
import { Checkbox, SxProps, Theme } from "@mui/material";
import {
  GridColDef,
  GridEditInputCell,
  GridPaginationModel,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridValidRowModel,
  GridValueGetterParams,
  GridValueSetterParams,
} from "@mui/x-data-grid";

/** Cell class applied when a cell's value differs from its last loaded or saved value. */
export const GRID_DIRTY_CELL_CLASS = "oc-grid-dirty-cell";
/** Cell class applied when a cell's current value fails validation. Wins over dirty when both apply. */
export const GRID_INVALID_CELL_CLASS = "oc-grid-invalid-cell";

/** Default page size for editable grids. */
export const EDITABLE_GRID_DEFAULT_PAGE_SIZE = 25;
/** Default page size options for editable grids. */
export const EDITABLE_GRID_PAGE_SIZE_OPTIONS: number[] = [25, 50, 100];

/**
 * Styles for the dirty and invalid cell classes. The invalid rule is listed after the
 * dirty rule so it wins when a cell carries both classes.
 */
export const editableGridCellSx: SxProps<Theme> = {
  [`& .${GRID_DIRTY_CELL_CLASS}`]: { backgroundColor: "rgba(255, 193, 7, 0.18)" },
  [`& .${GRID_INVALID_CELL_CLASS}`]: {
    backgroundColor: "rgba(244, 67, 54, 0.28)",
    outline: "1px solid rgba(244, 67, 54, 0.7)",
    outlineOffset: "-1px",
  },
};

/** Build the space-separated cell class string for the given dirty and invalid flags. */
export const cellHighlightClassName = (dirty: boolean, invalid: boolean): string =>
  [dirty ? GRID_DIRTY_CELL_CLASS : "", invalid ? GRID_INVALID_CELL_CLASS : ""].filter(Boolean).join(" ");

/**
 * Per-column defaults for a data column: centered header and cells, not sortable so the
 * grid always shows the stored row order. Anything in `col` overrides these defaults.
 */
export const dataCol = <TRow extends GridValidRowModel = GridValidRowModel>(
  col: GridColDef<TRow>
): GridColDef<TRow> => ({
  align: "center",
  headerAlign: "center",
  sortable: false,
  ...col,
});

export interface IIntegerEditCellInputProps {
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Build a `renderEditCell` for a numeric column whose inline editor carries HTML5
 * min/max/step attributes. `step` defaults to 1.
 */
export const integerEditCell =
  <TRow extends GridValidRowModel = GridValidRowModel>(inputProps: IIntegerEditCellInputProps = {}) =>
  (params: GridRenderEditCellParams<TRow>): JSX.Element =>
    createElement(GridEditInputCell, { ...params, inputProps: { step: 1, ...inputProps } });

/** Set one field on a row, returning a new row object. */
const withField = <TRow extends GridValidRowModel>(row: TRow, field: string, value: unknown): TRow =>
  ({ ...row, [field]: value }) as TRow;

export interface IEditableIntegerColumnOptions {
  min?: number;
  max?: number;
  flex?: number;
  /** Defaults to true. */
  editable?: boolean;
}

/**
 * Editable integer column. A committed value is rounded to the nearest integer and clamped
 * to `[min, max]`. A blank or non-numeric edit leaves the stored value unchanged.
 */
export const editableIntegerColumn = <TRow extends GridValidRowModel = GridValidRowModel>(
  field: string,
  headerName: string,
  opts: IEditableIntegerColumnOptions = {}
): GridColDef<TRow> => {
  const { min, max, flex, editable = true } = opts;
  const snap = (value: unknown, previous: unknown): unknown => {
    const n =
      typeof value === "number" ? value : value === "" || value === null || value === undefined ? NaN : Number(value);
    if (!Number.isFinite(n)) return previous;
    let result = Math.round(n);
    if (min !== undefined) result = Math.max(min, result);
    if (max !== undefined) result = Math.min(max, result);
    return result;
  };
  return dataCol<TRow>({
    field,
    headerName,
    flex,
    editable,
    type: "number",
    valueSetter: (params: GridValueSetterParams<TRow>) =>
      withField(params.row, field, snap(params.value, (params.row as Record<string, unknown>)[field])),
    renderEditCell: integerEditCell<TRow>({ min, max }),
  });
};

export interface IEditableBooleanColumnOptions {
  flex?: number;
  /** Defaults to true. */
  editable?: boolean;
  /**
   * Render a checkbox that toggles on a single click. The built-in boolean cell shows an icon and
   * only reveals a checkbox once the cell enters edit mode.
   */
  alwaysVisibleCheckbox?: boolean;
}

/**
 * Always-visible checkbox cell. The toggle runs through the grid's edit cycle so the change still
 * arrives via processRowUpdate and onRowsChange, and it is disabled wherever the cell is not editable.
 */
const toggleCheckboxCell = <TRow extends GridValidRowModel>(params: GridRenderCellParams<TRow, boolean>): JSX.Element =>
  createElement(Checkbox, {
    size: "small",
    checked: params.value === true,
    disabled: params.isEditable === false,
    inputProps: { "aria-label": params.colDef.headerName ?? params.field },
    onClick: (event: React.MouseEvent) => event.stopPropagation(),
    onChange: (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const { id, field, api } = params;
      void (async (): Promise<void> => {
        api.startCellEditMode({ id, field });
        await api.setEditCellValue({ id, field, value: checked });
        api.stopCellEditMode({ id, field });
      })();
    },
  });

/**
 * Editable boolean column using the built-in "boolean" type: a check or cross icon in view
 * mode and a checkbox while the cell is in edit mode.
 */
export const editableBooleanColumn = <TRow extends GridValidRowModel = GridValidRowModel>(
  field: string,
  headerName: string,
  opts: IEditableBooleanColumnOptions = {}
): GridColDef<TRow> => {
  const { flex, editable = true, alwaysVisibleCheckbox = false } = opts;
  return dataCol<TRow>({
    field,
    headerName,
    flex,
    editable,
    type: "boolean",
    ...(alwaysVisibleCheckbox ? { renderCell: toggleCheckboxCell<TRow> } : {}),
  });
};

export interface IReadOnlyTextColumnOptions<TRow extends GridValidRowModel = GridValidRowModel> {
  flex?: number;
  valueGetter?: (params: GridValueGetterParams<TRow>) => unknown;
}

/** Read-only text column, optionally deriving its display value through `valueGetter`. */
export const readOnlyTextColumn = <TRow extends GridValidRowModel = GridValidRowModel>(
  field: string,
  headerName: string,
  opts: IReadOnlyTextColumnOptions<TRow> = {}
): GridColDef<TRow> => {
  const { flex, valueGetter } = opts;
  return dataCol<TRow>({ field, headerName, flex, editable: false, valueGetter });
};

export interface IEditableIsoDateColumnOptions {
  flex?: number;
  /** Defaults to true. */
  editable?: boolean;
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse a "YYYY-MM-DD" string into a local-time Date at midnight, or null when malformed. */
export const isoDateToLocalDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Format a Date's local calendar day as "YYYY-MM-DD". */
export const localDateToIsoDate = (date: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Editable date column over a "YYYY-MM-DD" string field. The grid edits a local Date via the
 * built-in date editor; the committed Date is written back as "YYYY-MM-DD". Clearing the
 * editor or entering an invalid date leaves the stored value unchanged.
 */
export const editableIsoDateColumn = <TRow extends GridValidRowModel = GridValidRowModel>(
  field: string,
  headerName: string,
  opts: IEditableIsoDateColumnOptions = {}
): GridColDef<TRow> => {
  const { flex, editable = true } = opts;
  return dataCol<TRow>({
    field,
    headerName,
    flex,
    editable,
    type: "date",
    valueGetter: (params: GridValueGetterParams<TRow>) => isoDateToLocalDate(params.value),
    valueSetter: (params: GridValueSetterParams<TRow>) => {
      const value: unknown = params.value;
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) return params.row;
      return withField(params.row, field, localDateToIsoDate(value));
    },
  });
};

export interface IUseEditableGridPaginationArgs<TRow extends GridValidRowModel> {
  rows: TRow[];
  getRowId: (row: TRow) => GridRowId;
  /** Element wrapping the grid; its virtual scroller is scrolled after a focus request. */
  containerRef: RefObject<HTMLElement>;
  /** Defaults to EDITABLE_GRID_DEFAULT_PAGE_SIZE. */
  initialPageSize?: number;
}

interface IGridFocusRequest {
  rowId: GridRowId;
  scrollToBottom: boolean;
}

export interface IUseEditableGridPaginationResult {
  /** Pass to the grid's `paginationModel`. Always clamped to the current row count. */
  paginationModel: GridPaginationModel;
  /** Pass to the grid's `onPaginationModelChange`. */
  onPaginationModelChange: (model: GridPaginationModel) => void;
  /**
   * Ask the grid to show the page holding `rowId` on the next render. Call it right before
   * the rows update that introduces the row. `scrollToBottom` scrolls the page to its end
   * so an appended row is visible.
   */
  requestFocusRow: (rowId: GridRowId, scrollToBottom: boolean) => void;
}

/**
 * Controlled pagination for an editable grid. The page is clamped whenever rows are removed,
 * and a pending focus request moves the grid to the page holding the requested row.
 */
export const useEditableGridPagination = <TRow extends GridValidRowModel>(
  args: IUseEditableGridPaginationArgs<TRow>
): IUseEditableGridPaginationResult => {
  const { rows, getRowId, containerRef, initialPageSize = EDITABLE_GRID_DEFAULT_PAGE_SIZE } = args;
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: initialPageSize });
  // A focus request is consumed on the first render after it is made, against that render's rows.
  const [focusRequest, setFocusRequest] = useState<IGridFocusRequest | null>(null);
  // Identity changes once per consumed request that asked to scroll, so the effect below fires once each.
  const [scrollRequest, setScrollRequest] = useState<IGridFocusRequest | null>(null);

  const requestFocusRow = useCallback((rowId: GridRowId, scrollToBottom: boolean): void => {
    setFocusRequest({ rowId, scrollToBottom });
  }, []);

  // Clamp during render so the pagination footer never sees an out-of-range page.
  const maxPage = rows.length === 0 ? 0 : Math.ceil(rows.length / paginationModel.pageSize) - 1;
  const safePage = Math.min(Math.max(paginationModel.page, 0), maxPage);

  // Reconcile the stored page with the current rows while rendering, so the page and the rows
  // commit together: a pending focus request jumps to the page holding its row; otherwise the
  // stored page is synced back to safePage.
  if (focusRequest) {
    const idx = rows.findIndex((r) => getRowId(r) === focusRequest.rowId);
    const desiredPage = idx >= 0 ? Math.floor(idx / paginationModel.pageSize) : safePage;
    setFocusRequest(null);
    if (desiredPage !== paginationModel.page) setPaginationModel({ ...paginationModel, page: desiredPage });
    if (focusRequest.scrollToBottom) setScrollRequest(focusRequest);
  } else if (safePage !== paginationModel.page) {
    setPaginationModel({ ...paginationModel, page: safePage });
  }

  const effectivePaginationModel =
    safePage === paginationModel.page ? paginationModel : { ...paginationModel, page: safePage };

  // Scroll the grid's virtual scroller to the bottom of the page once the requested row is
  // committed. Two frames let the new page lay out first.
  useEffect(() => {
    if (!scrollRequest) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scroller = containerRef.current?.querySelector<HTMLDivElement>(".MuiDataGrid-virtualScroller");
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    });
  }, [scrollRequest, containerRef]);

  return { paginationModel: effectivePaginationModel, onPaginationModelChange: setPaginationModel, requestFocusRow };
};
